import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { NEWSROOM_RETURN, newsroomDetailState } from "@/lib/newsroomNavigation";
import { publicApi } from "@/lib/api";
import { getPostThumbnailUrl } from "@/lib/html";
import type { TodayItem } from "@/lib/types";

const HOME_TODAY_MAX_ITEMS = 12;

function TodayCardPlaceholder() {
  return (
    <div aria-hidden>
      <div className="rounded-lg aspect-[4/3] mb-3" />
      <div className="h-4 mb-1" />
      <div className="h-10" />
    </div>
  );
}

export default function BioconToday() {
  const [items, setItems] = useState<TodayItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicApi.get<TodayItem[]>("/public/news/today");
      setItems((res.data || []).slice(0, HOME_TODAY_MAX_ITEMS));
    } catch {
      // 데이터 없을 경우 빈 목록 유지
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const slots = Array.from(
    { length: HOME_TODAY_MAX_ITEMS },
    (_, i) => items[i] ?? null,
  );

  return (
    <div className="w-full h-full bg-white rounded-xl p-6 md:p-8 border border-gray-100 hover:border-[#1977cc]/30 transition-colors">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-1 h-5 bg-[#1977cc] rounded-full" />
          <h2 className="text-lg font-bold text-[#2c4964]">Biocon Today</h2>
        </div>
        <div className="h-px bg-gray-100" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading
          ? Array.from({ length: HOME_TODAY_MAX_ITEMS }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-100 rounded-lg aspect-[4/3] mb-3" />
                <div className="h-3 bg-gray-100 rounded w-1/3 mb-2" />
                <div className="h-4 bg-gray-100 rounded w-full" />
              </div>
            ))
          : slots.map((item, index) =>
              item ? (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.08,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <Link
                    to={`/newsroom/today/${item.id}`}
                    state={newsroomDetailState(NEWSROOM_RETURN.home)}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg bg-gray-50 aspect-[4/3] mb-3 ring-1 ring-transparent group-hover:ring-[#1977cc]/30 transition-all duration-300">
                      <img
                        src={getPostThumbnailUrl(item.imageUrl, item.content)}
                        alt={item.title}
                        className="w-full h-full object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-[#1977cc]/0 group-hover:bg-[#1977cc]/15 transition-colors duration-300" />
                    </div>
                    <p className="text-xs text-[#1977cc] font-medium mb-1">{item.date}</p>
                    <h3 className="text-sm font-medium text-[#2c4964] group-hover:text-[#1977cc] transition-colors leading-snug line-clamp-2">
                      {item.title}
                    </h3>
                  </Link>
                </motion.div>
              ) : (
                <TodayCardPlaceholder key={`empty-${index}`} />
              ),
            )}
      </div>
    </div>
  );
}
